/**
 * Formulaire contact — validation, champ Objet libre.
 * V1 : envoi Shopify natif · V2 (endpoint) : Worker + PJ optionnelles.
 */
(() => {
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const MAX_FILES = 5;
  const MAX_FILE_BYTES = 10 * 1024 * 1024;
  const RECAP_KEY = 'tb_contact_recap';

  class ContactForm extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.root = this.querySelector('[data-cf-root]');
      this.form = this.querySelector('form.tb-contact__shopify-form') || this.querySelector('form');
      this.successEl = this.querySelector('[data-cf-success]');
      this.formView = this.querySelector('[data-cf-form-view]');
      this.announce = this.querySelector('[data-cf-announce]');
      this.formError = this.querySelector('[data-cf-form-error]');
      this.submitBtn = this.querySelector('[data-cf-submit]');
      this.submitLabel = this.querySelector('[data-cf-submit-label]');
      this.fileInput = this.querySelector('[data-cf-files]');
      this.fileList = this.querySelector('[data-cf-file-list]');
      this.againBtn = this.querySelector('[data-cf-again]');
      this.recapEl = this.querySelector('[data-cf-recap]');
      this.messageEl = this.querySelector('[data-cf-message]');

      this.endpoint = (this.dataset.endpoint || '').trim();
      this.sendMode = this.dataset.sendMode || (this.endpoint ? 'worker' : 'native');
      this.msgs = this._readMsgs();
      this.files = [];
      this._defaultSubmit = this.submitLabel ? this.submitLabel.textContent : 'Envoyer';

      this._revealIfNeeded();
      this._bindFields();
      this._autosize();

      this.form?.addEventListener('submit', (e) => this._onSubmit(e));
      this.fileInput?.addEventListener('change', () => this._onFilesPicked());
      this.againBtn?.addEventListener('click', (e) => this._onAgain(e));
      window.addEventListener('resize', () => this._autosize());

      if (this.root?.classList.contains('is-success') || this.announce?.dataset.cfPosted) {
        this._hydrateSuccessFromStorage();
      }
    }

    /** Formulaire masqué sur FAQ jusqu’au clic / succès / ancre #ContactForm */
    _revealIfNeeded() {
      if (!this.hasAttribute('data-hc-contact-form')) return;
      const hash = (window.location.hash || '').replace(/^#/, '');
      const success =
        this.root?.classList.contains('is-success') || Boolean(this.announce?.dataset.cfPosted);
      if (success || hash === 'ContactForm' || hash === 'contact-form') {
        this.hidden = false;
        this.classList.add('is-revealed');
      }
    }

    static revealHost(host) {
      if (!host) return;
      host.hidden = false;
      host.classList.add('is-revealed');
      try {
        history.replaceState(null, '', '#ContactForm');
      } catch (e) {
        /* ignore */
      }
      const top = host.getBoundingClientRect().top + window.scrollY - 24;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      const focusable = host.querySelector(
        'input:not([type="hidden"]):not([tabindex="-1"]), textarea'
      );
      window.setTimeout(() => focusable?.focus({ preventScroll: true }), 350);
    }

    _readMsgs() {
      const el = this.querySelector('script[data-cf-msgs]');
      try {
        return JSON.parse(el?.textContent || '{}');
      } catch {
        return {};
      }
    }

    _bindFields() {
      this.form?.querySelectorAll('[data-cf-field]').forEach((field) => {
        const input = field.querySelector('input:not([type="hidden"]), textarea');
        input?.addEventListener('input', () => {
          if (field.classList.contains('is-invalid')) this._clearError(field);
          if (input === this.messageEl) this._autosize();
        });
      });
    }

    _autosize() {
      const ta = this.messageEl;
      if (!ta) return;
      ta.style.height = 'auto';
      ta.style.height = `${Math.min(Math.max(ta.scrollHeight + 2, 112), 460)}px`;
    }

    _onFilesPicked() {
      const picked = [...(this.fileInput?.files || [])];
      for (const file of picked) {
        if (this.files.length >= MAX_FILES) break;
        if (file.size > MAX_FILE_BYTES) continue;
        if (this.files.some((f) => f.name === file.name && f.size === file.size)) continue;
        this.files.push(file);
      }
      if (this.fileInput) this.fileInput.value = '';
      this._renderFiles();
    }

    _renderFiles() {
      if (!this.fileList) return;
      this.fileList.innerHTML = '';
      this.fileList.hidden = this.files.length === 0;
      this.files.forEach((file, index) => {
        const li = document.createElement('li');
        li.innerHTML = `
          <span class="tb-contact__file-dot" aria-hidden="true"></span>
          <span class="tb-contact__file-name"></span>
          <span class="tb-contact__file-size"></span>
          <button type="button" class="tb-contact__file-remove" aria-label="Retirer">×</button>
        `;
        li.querySelector('.tb-contact__file-name').textContent = file.name;
        li.querySelector('.tb-contact__file-size').textContent = `• ${this._fmtSize(file.size)}`;
        li.querySelector('button').addEventListener('click', () => {
          this.files.splice(index, 1);
          this._renderFiles();
        });
        this.fileList.appendChild(li);
      });
    }

    _fmtSize(bytes) {
      if (bytes < 1024) return `${bytes} o`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
      return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo`;
    }

    _values() {
      const fd = new FormData(this.form);
      return {
        name: String(fd.get('contact[name]') || fd.get('name') || '').trim(),
        email: String(fd.get('contact[email]') || fd.get('email') || '').trim(),
        order_number: String(
          fd.get('contact[N° commande]') ||
            fd.get('contact[order_number]') ||
            fd.get('order_number') ||
            ''
        ).trim(),
        subject: String(
          fd.get('contact[Objet]') ||
            fd.get('contact[Sujet]') ||
            fd.get('contact[subject]') ||
            fd.get('subject') ||
            ''
        ).trim(),
        message: String(fd.get('contact[body]') || fd.get('message') || '').trim(),
      };
    }

    _validate(v) {
      const e = {};
      const m = this.msgs;
      if (!v.name) e.name = m.errName || '';
      if (!v.email) e.email = m.errEmail || '';
      else if (!EMAIL_RE.test(v.email)) e.email = m.errEmailInvalid || '';
      if (!v.subject) e.subject = m.errSubject || '';
      if (!v.message) e.message = m.errMessage || '';
      return e;
    }

    _showErrors(errors) {
      this.form?.querySelectorAll('[data-cf-field]').forEach((field) => {
        const key = field.dataset.cfField;
        if (errors[key]) this._setError(field, errors[key]);
        else this._clearError(field);
      });
      const firstKey = ['name', 'email', 'subject', 'message'].find((k) => errors[k]);
      if (firstKey) {
        const field = this.form?.querySelector(`[data-cf-field="${firstKey}"]`);
        const focusable =
          field?.querySelector('input:not([type="hidden"]), textarea') || null;
        focusable?.focus({ preventScroll: false });
      }
      if (this.announce) {
        this.announce.textContent = this.msgs.errAnnounce || '';
      }
    }

    _setError(field, message) {
      field.classList.add('is-invalid');
      let err = field.querySelector('[data-cf-error]');
      if (!err) {
        err = document.createElement('p');
        err.className = 'tb-contact__error';
        err.dataset.cfError = '';
        err.setAttribute('role', 'alert');
        field.appendChild(err);
      }
      err.textContent = message;
      const control = field.querySelector('input:not([type="hidden"]), textarea');
      control?.setAttribute('aria-invalid', 'true');
    }

    _clearError(field) {
      field.classList.remove('is-invalid');
      field.querySelector('[data-cf-error]')?.remove();
      const control = field.querySelector('input:not([type="hidden"]), textarea');
      control?.removeAttribute('aria-invalid');
    }

    async _onSubmit(e) {
      if (this.formError) {
        this.formError.hidden = true;
        this.formError.textContent = '';
      }

      const values = this._values();
      const errors = this._validate(values);
      if (Object.keys(errors).length) {
        e.preventDefault();
        this._showErrors(errors);
        return;
      }

      if (this.sendMode === 'worker') {
        e.preventDefault();
        await this._submitWorker(values);
        return;
      }

      // V1 native — laisser le submit navigateur (hCaptcha Shopify + POST contact)
      // Ne pas preventDefault / form.submit() / disable bouton : ça coupe l’envoi.
      try {
        sessionStorage.setItem(
          RECAP_KEY,
          JSON.stringify({ subject: values.subject, email: values.email })
        );
      } catch {
        /* ignore */
      }
      // Champ vide = pas dans le mail Shopify
      const orderInput = this.form?.querySelector('[data-cf-order]');
      if (orderInput && !String(orderInput.value || '').trim()) {
        orderInput.disabled = true;
      }
      if (this.submitLabel) {
        this.submitLabel.textContent = this.msgs.sending || 'Envoi…';
      }
    }

    async _submitWorker(values) {
      if (!this.endpoint) {
        this._showFormError(this.msgs.errSend || '');
        return;
      }

      this._setSending(true);

      const body = new FormData();
      body.set('name', values.name);
      body.set('email', values.email);
      body.set('order_number', values.order_number);
      body.set('subject', values.subject);
      body.set('message', values.message);
      body.set('locale', this.dataset.locale || '');
      body.set('page_url', window.location.href);
      this.files.forEach((file) => body.append('attachments', file, file.name));

      try {
        const res = await fetch(this.endpoint, {
          method: 'POST',
          body,
          headers: { Accept: 'application/json' },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.ok) {
          throw new Error(data.error || 'send_failed');
        }
        this._showSuccess(values);
      } catch {
        this._showFormError(this.msgs.errSend || '');
      } finally {
        this._setSending(false);
      }
    }

    _setSending(on) {
      if (!this.submitBtn || !this.submitLabel) return;
      this.submitBtn.disabled = on;
      this.submitBtn.classList.toggle('is-sending', on);
      this.submitLabel.textContent = on
        ? this.msgs.sending || 'Envoi…'
        : this._defaultSubmit;
    }

    _showFormError(message) {
      if (!this.formError) return;
      this.formError.hidden = !message;
      this.formError.textContent = message;
    }

    _showSuccess(values) {
      this.root?.classList.add('is-success');
      if (this.formView) this.formView.hidden = true;
      if (this.successEl) this.successEl.hidden = false;
      this._fillRecap(values.subject, values.email);
      if (this.announce) this.announce.textContent = this.msgs.successAnnounce || '';
      this.successEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.againBtn?.focus();
    }

    _hydrateSuccessFromStorage() {
      let subject = '';
      let email = '';
      try {
        const raw = sessionStorage.getItem(RECAP_KEY);
        if (raw) {
          const data = JSON.parse(raw);
          subject = data.subject || '';
          email = data.email || '';
          sessionStorage.removeItem(RECAP_KEY);
        }
      } catch {
        /* ignore */
      }
      if (!email && this.recapEl?.textContent) {
        /* Liquid a déjà mis un récap partiel */
      } else if (email) {
        this._fillRecap(subject, email);
      }
      if (this.announce) this.announce.textContent = this.msgs.successAnnounce || '';
      this.successEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    _fillRecap(subject, email) {
      if (!this.recapEl || !email) return;
      const tpl = this.msgs.recapTemplate || '{subject} · réponse à {email}';
      this.recapEl.textContent = tpl
        .replace('{subject}', subject || 'Contact')
        .replace('{email}', email);
    }

    _onAgain(e) {
      if (this.sendMode === 'native') {
        // lien <a> — navigation native vers page.url
        return;
      }
      e.preventDefault();
      this.root?.classList.remove('is-success');
      if (this.successEl) this.successEl.hidden = true;
      if (this.formView) this.formView.hidden = false;
      this.form?.reset();
      this.files = [];
      this._renderFiles();
      this.form?.querySelectorAll('[data-cf-field]').forEach((f) => this._clearError(f));
      this._showFormError('');
      this._autosize();
      this.formView?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.form?.querySelector('input[name="contact[name]"]')?.focus();
    }
  }

  if (!customElements.get('contact-form-section')) {
    customElements.define('contact-form-section', ContactForm);
  }

  window.tbRevealContactForm = () => {
    const host = document.querySelector('[data-hc-contact-form]');
    if (host && typeof ContactForm.revealHost === 'function') {
      ContactForm.revealHost(host);
    }
  };
})();
